import React, { useEffect, useMemo, useRef, useState } from "react";
import DataTable from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import { FiEdit } from "react-icons/fi";
import { AiOutlineDelete } from "react-icons/ai";
import { LuFilter } from "react-icons/lu";
import { Col, FormGroup, Modal, ModalBody, Row } from "react-bootstrap";
import DeleteModal from "../../components/DeleteModal";
import { useDispatch, useSelector } from "react-redux";
import {
  createArea,
  getAreaList,
  updateArea,
  changeAreaStatus
} from "../../../../redux/slices/areaSlice";

const Area = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [forUpdate, setForUpdate] = useState(false);
  const [areaId, setAreaId] = useState("");
  const [showAreaModal, setShowAreaModal] = useState(false);
  const [areaName, setAreaName] = useState("");
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState("");
  const [searchText, setSearchText] = useState("");
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
  const dispatch = useDispatch();
  const { admin } = useSelector((state) => state.userSlice);
  const { areas, refetch } = useSelector((state) => state.areaSlice);
  const prevProps = useRef({ refetch }).current;

  useEffect(() => {
    setIsLoading(true);
    dispatch(getAreaList(setIsLoading));
  }, []);

  useEffect(() => {
    if (prevProps.refetch != refetch) {
      dispatch(getAreaList(setIsLoading));
    }
    return () => {
      prevProps.refetch = refetch;
    };
  }, [refetch]);

  const columns = [
    {
      name: "Created At",
      selector: (row) => row.createdAt,
      sortable: true,
    },
    {
      name: "Area Name",
      selector: (row) => row.areaName,
      sortable: true,
    },
    {
      name: "Status",
      selector: (row) => row.status,
      sortable: true,
    },
    ...(admin.role === "admin"
      ? [
        {
          name: "Actions",
          selector: (row) => row.action,
        },
      ]
      : admin.role === "subAdmin" &&
        admin?.areaAccess?.canEdit &&
        admin?.areaAccess?.canDelete
        ? [
          {
            name: "Actions",
            selector: (row) => row.action,
          },
        ]
        : []),
  ];

  const data = areas.map((area) => {
    const isDeleted = area.deleted;
    return {
      areaName: area.areaName,
      status: (
        <label
          className={
            area.active ? "badge badge-success" : "badge badge-danger"
          }
        >
          {area.active ? "Active" : "Inactive"}
        </label>
      ),
      createdAt: new Date(area.createdAt)
        .toLocaleDateString("en-US", {
          month: "2-digit",
          day: "2-digit",
          year: "numeric",
        })
        .replace(/\//g, "-"),
      action: (
        <div className="d-flex align-items-center justify-content-center" style={{ gap: "12px" }}>
          <div className="form-check form-switch" style={{ transform: "scale(1.3)" }}>
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              id={`switch-${area._id}`}
              checked={area.active}
              disabled={area.deleted}
              onChange={() => dispatch(changeAreaStatus(area._id))}
              style={{
                cursor: area.deleted ? "not-allowed" : "pointer",
                width: "2.4rem", // makes toggle wider
                height: "1.2rem",
              }}
            />
          </div>

          {(admin.role === "admin" ||
            (admin.role === "subAdmin" && admin?.areaAccess?.canEdit)) && (
              <FiEdit
                size={18}
                onClick={
                  !isDeleted
                    ? () => {
                      setShowAreaModal(true);
                      setAreaName(area.areaName);
                      setAreaId(area._id);
                      setForUpdate(true);
                    }
                    : null
                }
                style={{
                  cursor: isDeleted ? "not-allowed" : "pointer",
                  opacity: isDeleted ? 0.5 : 1,
                }}
              />
            )}

          {(admin.role === "admin" ||
            (admin.role === "subAdmin" && admin?.areaAccess?.canDelete)) && (
              <AiOutlineDelete
                size={20}
                onClick={
                  !isDeleted
                    ? () => {
                      setDeleteId(area?._id);
                      setDeleteModal(true);
                    }
                    : null
                }
                style={{
                  cursor: isDeleted ? "not-allowed" : "pointer",
                  opacity: isDeleted ? 0.5 : 1,
                }}
              />
            )}
        </div>
      ),
    };
  });

  const filteredData = useMemo(() => {
    return data.filter((item) => {
      const matchesSearchText = item?.areaName
        ?.toLowerCase()
        ?.includes(searchText?.toLowerCase());

      return matchesSearchText;
    });
  }, [data, searchText]);

  const handleSearch = (value) => {
    setSearchText(value);
    setResetPaginationToggle(!resetPaginationToggle);
  };

  const handleAddAreaName = () => {
    if (forUpdate) {
      dispatch(updateArea(areaId, { areaName }));
    } else {
      dispatch(createArea({ areaName }));
    }
    setShowAreaModal(false);
    setAreaName("");
    setAreaId("");
    setForUpdate(false);
  };

  return (
    <>
      {isLoading ? (
        <div className="w-100 d-flex">
          <div class="spinner-border text-primary mx-auto" role="status"></div>
        </div>
      ) : (
        <>
          <div>
            <div className="page-header">
              <h3 className="page-title">Area List</h3>
            </div>
            <div className="row">
              <div className="col-lg-12 grid-margin stretch-card">
                <div className="card">
                  <div className="card-body py-4">
                    <>
                      <DataTable
                        columns={columns}
                        data={filteredData}
                        pagination
                        paginationPerPage={20}
                        paginationRowsPerPageOptions={[
                          10, 20, 30, 50, 100, 200, 500,
                        ]}
                        pointerOnHover
                        subHeader
                        subHeaderAlign="left"
                        subHeaderComponent={
                          <>
                            <Row className="mb-3 w-100">
                              <Col
                                md={8}
                                className="d-flex align-items-start flex-column gap-2"
                              >
                                <span className="d-flex align-items-center gap-2 me-3 filterby_label">
                                  <LuFilter />
                                  Filter By :
                                </span>
                                <div className="table_filter">
                                  <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search by name"
                                    value={searchText}
                                    onChange={(e) =>
                                      handleSearch(e.target.value)
                                    }
                                  />
                                </div>
                              </Col>
                              <Col md={4} className="d-flex align-items-end">
                                <button
                                  className="btn_primary_outline_sm ms-auto"
                                  onClick={() => setShowAreaModal(true)}
                                >
                                  Add More Areas
                                </button>
                              </Col>
                            </Row>
                          </>
                        }
                      />
                    </>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <DeleteModal
            forArea
            deleteId={deleteId}
            deleteModal={deleteModal}
            setDeleteModal={setDeleteModal}
          />
          <Modal
            show={showAreaModal}
            size="md"
            aria-labelledby="contained-modal-title-vcenter"
            centered
          >
            <ModalBody>
              <h5 className="mb-3">
                {forUpdate ? "Update Area Name" : "Add Area Name"}
              </h5>
              <FormGroup className="mb-3">
                <label>Area Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={areaName}
                  onChange={(e) => setAreaName(e.target.value)}
                  placeholder="Enter Area Name"
                  required
                />
              </FormGroup>
            </ModalBody>
            <Modal.Footer>
              {isLoading ? (
                <div className="w-100 d-flex">
                  <div
                    className="spinner-border text-primary mx-auto"
                    role="status"
                  ></div>
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    className="btn_secondary"
                    onClick={() => {
                      setShowAreaModal(false);
                      setAreaName("");
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn_primary"
                    onClick={handleAddAreaName}
                  >
                    {forUpdate ? "Update" : "Add"}
                  </button>
                </>
              )}
            </Modal.Footer>
          </Modal>
        </>
      )}
    </>
  );
};

export default Area;
